import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { ThemeProvider } from './contexts/ThemeContext'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { AssessmentProvider } from './contexts/AssessmentContext'
import { PolicyProvider } from './contexts/PolicyContext'
import { AIJobsProvider } from './contexts/AIJobsContext'
import { IdleTimeoutProvider } from './features/auth/components/IdleTimeoutProvider'
import ClientOnboardingGuard from './features/auth/components/ClientOnboardingGuard'
import ProtectedRoute from './features/auth/guards/ProtectedRoute'
import RoleBasedRoute from './features/auth/guards/RoleBasedRoute'
import Layout from './features/admin/components/Layout'
import PortalLayout from './features/portal/components/PortalLayout'
import Clients from './features/admin/pages/Clients'
import ClientPortal from './features/admin/pages/ClientPortal'
import NewAssessment from './features/assessment/pages/NewAssessment'
import TechnologyStack from './features/assessment/pages/TechnologyStack'
import Assessment from './features/assessment/pages/Assessment'
import Dashboard from './features/admin/pages/Dashboard'
import ClientUsersAdmin from './features/admin/pages/ClientUsersAdmin'
import Settings from './features/admin/pages/Settings'
import PolicyLibrary from './features/policies/pages/PolicyLibrary'
import ClientPoliciesAdmin from './features/policies/pages/ClientPoliciesAdmin'
import ClientPolicies from './features/policies/pages/ClientPolicies'
import ClientEnrichment from './features/demo/pages/ClientEnrichment'
import Login from './features/auth/pages/Login'
import Signup from './features/auth/pages/Signup'
import ForgotPassword from './features/auth/pages/ForgotPassword'
import ResetPassword from './features/auth/pages/ResetPassword'
import AuthCallback from './features/auth/pages/AuthCallback'
import InviteVerify from './features/auth/pages/InviteVerify'
import Welcome from './features/auth/pages/Welcome'
import PortalHome from './features/portal/pages/PortalHome'
import PortalResults from './features/portal/pages/PortalResults'
import PortalM365Report from './features/portal/pages/PortalM365Report'
import PortalPlan from './features/portal/pages/PortalPlan'
import PortalPolicies from './features/portal/pages/PortalPolicies'
import PortalSecurity from './features/portal/pages/PortalSecurity'
import PortalVault from './features/portal/pages/PortalVault'
import PortalActivityLog from './features/portal/pages/PortalActivityLog'
import PortalIntake from './features/portal/pages/PortalIntake'
import AuditLogs from './features/admin/pages/AuditLogs'
import RegulatoryUpdates from './features/admin/pages/RegulatoryUpdates'
import { OrgProvider } from './contexts/OrgContext'
import GlobalAdminLayout from './features/global-admin/components/GlobalAdminLayout'
import GlobalAdminGuard from './features/global-admin/components/GlobalAdminGuard'
import GlobalDashboard from './features/global-admin/pages/GlobalDashboard'
import OrganizationManagement from './features/global-admin/pages/OrganizationManagement'
import OrganizationDetail from './features/global-admin/pages/OrganizationDetail'
import GlobalActivityLog from './features/global-admin/pages/GlobalActivityLog'
import GlobalSecurityEvents from './features/global-admin/pages/GlobalSecurityEvents'
import GlobalPlatformSettings from './features/global-admin/pages/GlobalPlatformSettings'
import GlobalNotifications from './features/global-admin/pages/GlobalNotifications'
import GlobalAppStatus from './features/global-admin/pages/GlobalAppStatus'
import GlobalPolicyTemplates from './features/global-admin/pages/GlobalPolicyTemplates'
import InsuranceReviewAudit from './features/global-admin/pages/InsuranceReviewAudit'

// Component to redirect client users to their portal
function ClientRedirect({ children }) {
  const { profile, assignedClient, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  // Client users should be redirected to their portal
  if (profile?.role === 'client' && assignedClient) {
    return <Navigate to="/portal" replace />
  }

  return children
}

// Wrapper that picks the right layout based on route
function LayoutWrapper({ children }) {
  const location = useLocation()

  // Global Admin has its own layout — no wrapper needed
  if (location.pathname.startsWith('/global')) {
    return <>{children}</>
  }

  if (location.pathname.startsWith('/portal')) {
    return <PortalLayout>{children}</PortalLayout>
  }

  return <Layout>{children}</Layout>
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Routes>
        {/* Demo routes (no auth required) */}
        <Route path="/demo/enrich" element={<ClientEnrichment />} />

        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/welcome" element={<Welcome />} />
        {/* Legacy routes — /invite redirects to new welcome flow */}
        <Route path="/invite" element={<Welcome />} />
        <Route path="/invite/verify" element={<InviteVerify />} />

        {/* Protected routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <ClientOnboardingGuard>
              <IdleTimeoutProvider>
                <OrgProvider>
                <AssessmentProvider>
                <PolicyProvider>
                <AIJobsProvider>
                  <LayoutWrapper>
                    <Routes>
                      {/* Admin-only routes */}
                      <Route path="/" element={
                        <ClientRedirect>
                          <Dashboard />
                        </ClientRedirect>
                      } />
                      <Route path="/clients" element={
                        <RoleBasedRoute allowedRoles={['admin', 'global_admin', 'org_admin', 'org_member']}>
                          <Clients />
                        </RoleBasedRoute>
                      } />
                      <Route path="/users" element={
                        <RoleBasedRoute allowedRoles={['admin', 'global_admin', 'org_admin', 'org_member']}>
                          <ClientUsersAdmin />
                        </RoleBasedRoute>
                      } />
                      <Route path="/client-users" element={<Navigate to="/users" replace />} />
                      <Route path="/org-members" element={<Navigate to="/users" replace />} />

                      <Route path="/policies" element={
                        <RoleBasedRoute allowedRoles={['admin', 'global_admin', 'org_admin', 'org_member']}>
                          <PolicyLibrary />
                        </RoleBasedRoute>
                      } />

                      <Route path="/regulatory-updates" element={
                        <RoleBasedRoute allowedRoles={['admin', 'global_admin', 'org_admin', 'org_member']}>
                          <RegulatoryUpdates />
                        </RoleBasedRoute>
                      } />

                      <Route path="/audit-logs" element={
                        <RoleBasedRoute allowedRoles={['admin', 'global_admin', 'org_admin', 'org_member']}>
                          <AuditLogs />
                        </RoleBasedRoute>
                      } />

                      {/* Settings - accessible by all authenticated users */}
                      <Route path="/settings" element={<Settings />} />

                      {/* Admin-only client management routes */}
                      <Route path="/clients/:clientId" element={
                        <RoleBasedRoute allowedRoles={['admin', 'global_admin', 'org_admin', 'org_member']}>
                          <ClientPortal />
                        </RoleBasedRoute>
                      } />
                      <Route path="/clients/:clientId/new-assessment" element={
                        <RoleBasedRoute allowedRoles={['admin', 'global_admin', 'org_admin', 'org_member']}>
                          <NewAssessment />
                        </RoleBasedRoute>
                      } />
                      <Route path="/clients/:clientId/technology-stack" element={
                        <RoleBasedRoute allowedRoles={['admin', 'global_admin', 'org_admin', 'org_member']}>
                          <TechnologyStack />
                        </RoleBasedRoute>
                      } />
                      <Route path="/clients/:clientId/policies" element={
                        <RoleBasedRoute allowedRoles={['admin', 'global_admin', 'org_admin', 'org_member']}>
                          <ClientPoliciesAdmin />
                        </RoleBasedRoute>
                      } />
                      <Route path="/assessment/:assessmentId" element={
                        <RoleBasedRoute allowedRoles={['admin', 'global_admin', 'org_admin', 'org_member']}>
                          <Assessment />
                        </RoleBasedRoute>
                      } />

                      {/* Client portal routes — /portal/* (client users, clientId from AuthContext) */}
                      <Route path="/portal" element={
                        <RoleBasedRoute allowedRoles={['client']}>
                          <PortalHome />
                        </RoleBasedRoute>
                      } />
                      <Route path="/portal/intake" element={
                        <RoleBasedRoute allowedRoles={['client']}>
                          <PortalIntake />
                        </RoleBasedRoute>
                      } />
                      <Route path="/portal/results" element={
                        <RoleBasedRoute allowedRoles={['client']}>
                          <PortalResults />
                        </RoleBasedRoute>
                      } />
                      <Route path="/portal/m365-report" element={
                        <RoleBasedRoute allowedRoles={['client']}>
                          <PortalM365Report />
                        </RoleBasedRoute>
                      } />
                      <Route path="/portal/plan" element={
                        <RoleBasedRoute allowedRoles={['client']}>
                          <PortalPlan />
                        </RoleBasedRoute>
                      } />
                      <Route path="/portal/policies" element={
                        <RoleBasedRoute allowedRoles={['client']}>
                          <PortalPolicies />
                        </RoleBasedRoute>
                      } />
                      <Route path="/portal/security" element={
                        <RoleBasedRoute allowedRoles={['client']}>
                          <PortalSecurity />
                        </RoleBasedRoute>
                      } />
                      <Route path="/portal/vault" element={
                        <RoleBasedRoute allowedRoles={['client']}>
                          <PortalVault />
                        </RoleBasedRoute>
                      } />
                      <Route path="/portal/activity" element={
                        <RoleBasedRoute allowedRoles={['client']}>
                          <PortalActivityLog />
                        </RoleBasedRoute>
                      } />

                      {/* Admin portal preview — /portal/:clientId/* */}
                      <Route path="/portal/:clientId" element={
                        <RoleBasedRoute allowedRoles={['admin', 'global_admin', 'org_admin', 'org_member', 'client']} requireClientAccess>
                          <PortalHome />
                        </RoleBasedRoute>
                      } />
                      <Route path="/portal/:clientId/intake" element={
                        <RoleBasedRoute allowedRoles={['admin', 'global_admin', 'org_admin', 'org_member', 'client']} requireClientAccess>
                          <PortalIntake />
                        </RoleBasedRoute>
                      } />
                      <Route path="/portal/:clientId/results" element={
                        <RoleBasedRoute allowedRoles={['admin', 'global_admin', 'org_admin', 'org_member', 'client']} requireClientAccess>
                          <PortalResults />
                        </RoleBasedRoute>
                      } />
                      <Route path="/portal/:clientId/m365-report" element={
                        <RoleBasedRoute allowedRoles={['admin', 'global_admin', 'org_admin', 'org_member', 'client']} requireClientAccess>
                          <PortalM365Report />
                        </RoleBasedRoute>
                      } />
                      <Route path="/portal/:clientId/plan" element={
                        <RoleBasedRoute allowedRoles={['admin', 'global_admin', 'org_admin', 'org_member', 'client']} requireClientAccess>
                          <PortalPlan />
                        </RoleBasedRoute>
                      } />
                      <Route path="/portal/:clientId/policies" element={
                        <RoleBasedRoute allowedRoles={['admin', 'global_admin', 'org_admin', 'org_member', 'client']} requireClientAccess>
                          <PortalPolicies />
                        </RoleBasedRoute>
                      } />
                      <Route path="/portal/:clientId/security" element={
                        <RoleBasedRoute allowedRoles={['admin', 'global_admin', 'org_admin', 'org_member', 'client']} requireClientAccess>
                          <PortalSecurity />
                        </RoleBasedRoute>
                      } />
                      <Route path="/portal/:clientId/vault" element={
                        <RoleBasedRoute allowedRoles={['admin', 'global_admin', 'org_admin', 'org_member', 'client']} requireClientAccess>
                          <PortalVault />
                        </RoleBasedRoute>
                      } />
                      <Route path="/portal/:clientId/activity" element={
                        <RoleBasedRoute allowedRoles={['admin', 'global_admin', 'org_admin', 'org_member', 'client']} requireClientAccess>
                          <PortalActivityLog />
                        </RoleBasedRoute>
                      } />
                      {/* Global Admin routes */}
                      <Route path="/global" element={
                        <GlobalAdminGuard>
                          <GlobalAdminLayout />
                        </GlobalAdminGuard>
                      }>
                        <Route index element={<GlobalDashboard />} />
                        <Route path="organizations" element={<OrganizationManagement />} />
                        <Route path="organizations/:orgId" element={<OrganizationDetail />} />
                        <Route path="activity" element={<GlobalActivityLog />} />
                        <Route path="security" element={<GlobalSecurityEvents />} />
                        <Route path="status" element={<GlobalAppStatus />} />
                        <Route path="notifications" element={<GlobalNotifications />} />
                        <Route path="policy-templates" element={<GlobalPolicyTemplates />} />
                        <Route path="insurance-review-audit" element={<InsuranceReviewAudit />} />
                        <Route path="settings" element={<GlobalPlatformSettings />} />
                      </Route>
                    </Routes>
                  </LayoutWrapper>
                </AIJobsProvider>
                </PolicyProvider>
                </AssessmentProvider>
                </OrgProvider>
              </IdleTimeoutProvider>
              </ClientOnboardingGuard>
            </ProtectedRoute>
          }
        />
        </Routes>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
